/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable max-len */
import { css } from '@emotion/css';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { HoverToReveal } from '^/commonComponents/ui/HoverToReveal';
import { CarInfoCardDesktop } from '^/components/CarInfoCard';
import { InsuranceInfoBox } from '^/components/InsuranceInfo/insuranceInfo';
import { font2 } from '^/constants/commonStyles';
import { VENDOR_LOGOS } from '^/constants/channel/logoImages.constants';
import { getCarName } from '^/util/getCarName';
import { translateSimplifiedServices } from '^/pages/Listing/common/translateServices';
import {
  isValidExtraEquipments,
  ReservationDetailed,
} from '^/types/ReservationDetailed/RawReservationDetailed';
import { ReservationDetailOfficeInfoCard } from './ReservationDetailDesktop.OfficeInfoCard';

import { flexSplitHorizontalInHalf } from './ReservationDetailDesktop.commonStuff';
import { parseRentDateTime } from '^/util/rentDateTime';
import { formatEquipmentString } from '^/util/formatEquipmentString';

export const ReservationDetailsVehicleScheduleInfo: React.FC<{
  data: ReservationDetailed;
}> = observer(function ReservationDetailsVehicleScheduleInfo({ data }) {
  const { t } = useTranslation();
  const { parsedPickupTime, parsedReturnTime, timeDiff } = parseRentDateTime(
    data?.pickup_datetime,
    data?.return_datetime,
    'YYYY.MM.DD (ddd) HH:mm',
  );

  const insurance = data.insurance_info_json;
  const services = data.services_json;
  const validEquips = isValidExtraEquipments(data.extra_equipments_json)
    ? data.extra_equipments_json
    : null;

  return (
    <div className="flex flex-col gap-[20px] mt-[10px]">
      <CarInfoCardDesktop
        base={{
          vendor: data.supplier,
          vendorLogoUrl: VENDOR_LOGOS[data.supplier],
          carImageUrl: data.image || '/NOT_FOUND_CAR.png',
          carSizeLabel: `[${t(data.size_car)}]`,
          carModelName: getCarName(data.actualModelName) || data.actualModelName || data.car_model,
          carSpecText: translateSimplifiedServices(services, t),
          footerNote: '실제 대여하실 차량과 다를 수 있습니다.',
        }}
      />

      <div className={flexSplitHorizontalInHalf}>
        <div className="flex flex-col gap-3">
          <p className={font2.Caption1_Bold}>예약 번호</p>
          <p className={font2.Caption1_Light}>{data.displayReservationId}</p>
        </div>
        <div className="flex flex-col gap-3">
          <p className={font2.Caption1_Bold}>대여/반납 일시</p>
          <p className={font2.Caption1_Light}>
            {`${parsedPickupTime} ~ ${parsedReturnTime} (${timeDiff})`}
          </p>
        </div>
      </div>
      <div
        className={[
          flexSplitHorizontalInHalf,
          css`
            position: relative;
          `,
        ].join(' ')}
      >
        <div className="flex flex-col gap-3">
          <p className={font2.Caption1_Bold}>보험 내용</p>
          <div
            className={[
              font2.Caption1_Light,
              css`
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 6px;
              `,
            ].join(' ')}
          >
            <span>{insurance.code ? t(`insurance_name.${insurance.code}`) : '기본 할인요금'}</span>
            <HoverToReveal
              toReveal={
                <InsuranceInfoBox
                  insuranceName={
                    insurance.code ? t(`insurance_name.${insurance.code}`) : '기본 할인요금'
                  }
                  insuranceServices={data.insuranceServices as any}
                />
              }
            />
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <p className={[font2.Caption1_Bold].join(' ')}>추가 옵션</p>
          <p className={font2.Caption1_Light}>
            {(validEquips?.info?.length || 0) > 0 ? (
              <span>{formatEquipmentString(validEquips?.info, t)}</span>
            ) : (
              <p>{t('none')}</p>
            )}
          </p>
        </div>
      </div>

      <div className={flexSplitHorizontalInHalf}>
        <ReservationDetailOfficeInfoCard
          title="대여 영업소"
          officeName={data.office_name}
          officeAddress={data.office_address}
          officePhone={data.office_phone}
          officeHours={data.office_time}
        />
        <ReservationDetailOfficeInfoCard
          title="반납 영업소"
          officeName={data.office_second_name}
          officeAddress={data.office_second_address}
          officePhone={data.office_second_phone}
          officeHours={data.office_second_time}
        />
      </div>
      {data.overseas_reservation_id && data.pickup_loc_description && (
        <div className={'flex flex-col gap-3'}>
          <p className={font2.Caption1_Bold}>픽업 및 방문 안내</p>
          <p className={font2.Caption1_Light}>{data.pickup_loc_description}</p>
        </div>
      )}
    </div>
  );
});
